// A module is evaluated once for the responsive ingredient section. Ownership
// stays with the Shopify section after its overlays move to body.
const sections = new Map();
const openOverlays = new Set();
const overlaySelector = '[data-bbw-ing-drawer], [data-bbw-ing2-sheet]';
const lockClasses = ['bbw-ing-lock', 'bbw-ing2-lock'];
let bodyState;

const topOverlay = () => Array.from(openOverlays).at(-1);

// A set's intro follows the selected product and hides when it has none.
function showIntro(intro, text) {
  if (!intro) return;
  intro.textContent = text || '';
  intro.hidden = !text;
}

function syncBodyLock() {
  for (const name of lockClasses) {
    document.body.classList.toggle(name, bodyState.classes.has(name) || Array.from(openOverlays).some(item => item.lockClass === name));
  }
  if (!openOverlays.size) {
    // Do not overwrite a padding change made by another component meanwhile.
    if (document.body.style.paddingRight === bodyState.appliedPadding) {
      document.body.style.setProperty('padding-right', bodyState.padding, bodyState.priority);
    }
    bodyState = undefined;
  }
}

function canFocus(element) {
  if (!element?.isConnected || element.disabled || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  for (let node = element; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

function bindSwitchers(owner, { on }) {
  const root = owner.querySelector('[data-bbw-pdp-ingredients]');
  if (!root) return;
  const panels = Array.from(root.querySelectorAll('[data-bbw-ing-panel]'));
  const tabs = Array.from(root.querySelectorAll('[data-bbw-ing-tab]'));
  const intro = root.querySelector('[data-bbw-ing-intro]');
  // The picker registers here so tab clicks, tab keys and sheet rows all
  // leave the picker showing the selected product.
  const listeners = [];

  function select(index, introText) {
    const panel = panels[index];
    if (!panel) return;
    tabs.forEach((tab, position) => {
      tab.classList.toggle('is-active', position === index);
      tab.setAttribute('aria-selected', position === index ? 'true' : 'false');
      tab.tabIndex = position === index ? 0 : -1;
    });
    panels.forEach((item, position) => {
      item.hidden = position !== index;
      item.classList.remove('is-entering');
    });
    void panel.offsetWidth;
    panel.classList.add('is-entering');
    showIntro(intro, introText);
    listeners.forEach(listener => listener(index));
  }

  tabs.forEach((tab, index) => on(tab, 'click', () => {
    if (!tab.classList.contains('is-active')) select(index, tab.dataset.ingIntro || tab.dataset.rowIntro);
    if (window.matchMedia('(max-width: 767px)').matches) {
      tab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }));
  tabs.forEach((tab, index) => on(tab, 'keydown', event => {
    let next = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    select(next, tabs[next].dataset.ingIntro || tabs[next].dataset.rowIntro);
    tabs[next].focus();
  }));
  return { select, onSelect: listener => listeners.push(listener) };
}

function bindOverlay(element, owner, { on, signal, cleanup }, switcher) {
  const sheet = element.hasAttribute('data-bbw-ing2-sheet');
  const pickerRoot = sheet ? owner.querySelector('[data-bbw-ing2]') : null;
  const picker = pickerRoot?.querySelector('[data-bbw-ing2-picker]');
  const rows = Array.from(element.querySelectorAll('[data-bbw-ing2-row]'));
  const openers = sheet ? (picker ? [picker] : []) : Array.from(owner.querySelectorAll('[data-bbw-ing-open]')).filter(opener => opener.dataset.ingDrawer === element.dataset.sectionId);
  if (!openers.length || (sheet && !rows.length)) return;

  // The placeholder preserves the original position, including a hidden panel.
  // On unload the closed overlay returns to its departing section, so Shopify
  // removes its content and the same DOM can also be initialized again safely.
  const placeholder = document.createComment('ingredient overlay');
  element.before(placeholder);
  document.body.append(element);
  let lastOpener;
  let fitTimer;

  function fitPills() {
    if (signal.aborted) return;
    const pills = element.querySelectorAll('.bbw-ing-drawer__pill');
    for (const pill of pills) pill.style.width = '';
    for (const pill of pills) {
      const name = pill.querySelector('.bbw-ing-drawer__pill-name');
      const benefit = pill.querySelector('.bbw-ing-drawer__pill-benefit');
      if (!name || !benefit) continue;
      const nr = name.getBoundingClientRect();
      const br = benefit.getBoundingClientRect();
      if (br.top < nr.bottom - 1) continue;
      const style = window.getComputedStyle(pill);
      const chrome = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) + parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
      pill.style.width = Math.ceil(Math.max(nr.width, br.width) + chrome) + 'px';
    }
  }

  const controller = {
    element,
    lockClass: sheet ? 'bbw-ing2-lock' : 'bbw-ing-lock',
    focus() {
      const target = sheet ? element.querySelector('.is-selected') || rows[0] : element.querySelector('.bbw-ing-drawer__close');
      // Focusing a panel during its slide must not scroll it into place.
      if (target) target.focus({ preventScroll: true });
    },
    close(returnFocus = true) {
      const wasOpen = openOverlays.delete(controller);
      element.classList.remove('is-open');
      element.setAttribute('aria-hidden', 'true');
      picker?.setAttribute('aria-expanded', 'false');
      if (!wasOpen) return;
      syncBodyLock();
      if (returnFocus) {
        if (topOverlay()) topOverlay().focus();
        else if (canFocus(lastOpener)) lastOpener.focus({ preventScroll: true });
        else if (element.contains(document.activeElement)) document.activeElement.blur();
      }
    },
  };

  function open(opener) {
    if (signal.aborted || !owner.isConnected) return;
    lastOpener = opener;
    if (!openOverlays.size) {
      bodyState = {
        padding: document.body.style.paddingRight,
        priority: document.body.style.getPropertyPriority('padding-right'),
        classes: new Set(lockClasses.filter(name => document.body.classList.contains(name))),
        appliedPadding: Math.max(0, window.innerWidth - document.documentElement.clientWidth) + 'px',
      };
      document.body.style.paddingRight = bodyState.appliedPadding;
    }
    openOverlays.delete(controller);
    openOverlays.add(controller);
    syncBodyLock();
    element.classList.add('is-open');
    element.setAttribute('aria-hidden', 'false');
    picker?.setAttribute('aria-expanded', 'true');
    if (!sheet) fitPills();
    controller.focus();
  }

  for (const opener of openers) on(opener, 'click', () => {
    if (sheet && openOverlays.has(controller)) controller.close();
    else open(opener);
  });
  for (const closer of element.querySelectorAll(sheet ? '[data-bbw-ing2-sheet-close]' : '[data-bbw-ing-close]')) {
    on(closer, 'click', () => controller.close());
  }
  on(document, 'keydown', event => {
    if (topOverlay() !== controller) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      // Other ingredient overlays must not also close on the same key event.
      event.stopImmediatePropagation();
      controller.close();
    } else if (event.key === 'Tab') {
      const focusable = Array.from(element.querySelectorAll('button, a[href], input, select, textarea, [tabindex]')).filter(node => node.tabIndex >= 0 && canFocus(node));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (!element.contains(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      }
    }
  });
  on(document, 'focusin', event => {
    if (topOverlay() === controller && !element.contains(event.target)) controller.focus();
  });

  if (sheet) {
    const panels = Array.from(pickerRoot.querySelectorAll('[data-bbw-ing2-panel]'));
    const pickerName = pickerRoot.querySelector('[data-bbw-ing2-picker-name]');
    const pickerArt = pickerRoot.querySelector('[data-bbw-ing2-picker-art]');
    const intro = pickerRoot.querySelector('[data-bbw-ing2-intro]');
    // The art container is stable; its image follows the selected product,
    // created when that product has art and removed when it has none.
    const showPickerArt = rowImg => {
      if (!pickerArt) return;
      let image = pickerArt.querySelector('[data-bbw-ing2-picker-img]');
      if (!rowImg) {
        image?.remove();
        return;
      }
      if (!image) {
        image = document.createElement('img');
        image.alt = '';
        image.loading = 'lazy';
        image.width = 80;
        image.height = 80;
        image.setAttribute('data-bbw-ing2-picker-img', '');
        pickerArt.append(image);
      }
      image.src = rowImg.currentSrc || rowImg.src;
    };
    const syncPicker = index => {
      const row = rows[index];
      if (!row) return;
      rows.forEach((item, position) => {
        item.classList.toggle('is-selected', position === index);
        if (position === index) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      if (pickerName) pickerName.textContent = row.dataset.rowName;
      showPickerArt(row.querySelector('[data-row-img]'));
    };
    switcher?.onSelect(syncPicker);
    rows.forEach((row, index) => on(row, 'click', () => {
      if (!panels[index]) return;
      if (switcher) switcher.select(index, row.dataset.rowIntro);
      else {
        panels.forEach((panel, position) => {
          panel.hidden = position !== index;
          panel.classList.remove('is-entering');
        });
        void panels[index].offsetWidth;
        panels[index].classList.add('is-entering');
        syncPicker(index);
      }
      showIntro(intro, row.dataset.rowIntro);
      controller.close();
    }));
  } else {
    on(window, 'resize', () => {
      window.clearTimeout(fitTimer);
      fitTimer = window.setTimeout(fitPills, 150);
    });
    // Font readiness cannot be cancelled, so fitPills checks section disposal.
    if (document.fonts?.ready) document.fonts.ready.then(fitPills);
  }

  cleanup(() => {
    window.clearTimeout(fitTimer);
    controller.close(false);
    if (placeholder.parentNode && owner.contains(placeholder)) placeholder.replaceWith(element);
    else element.remove();
  });
  return controller;
}

function initialize(scope) {
  const owners = new Map();
  for (const root of scope.querySelectorAll('[data-bbw-pdp-ingredients]')) {
    const owner = root.closest('.shopify-section') || document.getElementById('shopify-section-' + root.dataset.sectionId);
    if (owner && !owners.has(owner)) owners.set(owner, []);
  }
  for (const element of scope.querySelectorAll(overlaySelector)) {
    const id = element.dataset.ingOwner;
    const owner = document.getElementById('shopify-section-' + id);
    // Never discover a relocated overlay by a global drawer ID. Only the
    // current section's actual markup may supply its current overlay.
    if (!owner?.contains(element)) continue;
    if (!owners.has(owner)) owners.set(owner, []);
    owners.get(owner).push(element);
  }
  for (let [owner, elements] of owners) {
    const id = owner.id;
    const existing = sections.get(id);
    // Shopify can emit duplicate load events for the same wrapper after this
    // controller has moved its overlays to body. An empty local overlay list in
    // that case means "already initialized", not "replace the controller".
    if (existing?.owner === owner && !elements.length) continue;
    // New markup under an existing ID supersedes the previous section even
    // when a renderer omitted unload or reused the wrapper element.
    existing?.destroy();
    // Destroying returns the old controller's relocated overlays to this
    // section, so collect them again; otherwise an overlay that was in body
    // when the list was built (such as the picker sheet) is never rebound.
    if (existing) {
      const key = id.replace('shopify-section-', '');
      elements = Array.from(owner.querySelectorAll(overlaySelector)).filter(element => element.dataset.ingOwner === key);
    }
    const abort = new AbortController();
    const cleanups = [];
    const overlays = [];
    const context = {
      signal: abort.signal,
      on: (target, event, handler) => target.addEventListener(event, handler, { signal: abort.signal }),
      cleanup: callback => cleanups.push(callback),
    };
    const section = {
      owner,
      destroy() {
        const focusedOverlay = overlays.some(item => item.element.contains(document.activeElement));
        abort.abort();
        for (const cleanup of cleanups) cleanup();
        if (sections.get(id) === section) sections.delete(id);
        if (focusedOverlay) {
          if (topOverlay()) topOverlay().focus();
          else document.activeElement?.blur();
        }
      },
    };
    sections.set(id, section);
    const switcher = bindSwitchers(owner, context);
    for (const element of elements) {
      const overlay = bindOverlay(element, owner, context, switcher);
      if (overlay) overlays.push(overlay);
    }
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
else initialize(document);
document.addEventListener('shopify:section:load', event => initialize(event.target));
document.addEventListener('shopify:section:unload', event => {
  const section = sections.get(event.target.id);
  if (section?.owner === event.target) section.destroy();
});
